import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUserActivation, useUserDetail } from "~/features/admin/users/hooks";
import { useRetryUserLoginPreparation } from "~/features/admin/users/hooks/useRetryUserLoginPreparation";
import { toUserDetailUI } from "~/features/admin/users/utils/transformers";
import { UserDetailPresenter } from "./UserDetailPresenter";
import { UserFormDialog } from "./UserFormDialog";

interface UserDetailProps {
  userId: string;
  /** Back action: close the sheet (peek) or navigate to the list (full page). */
  onBack: () => void;
}

/**
 * Reusable from both the full-page route and the Peek side panel. Owns data,
 * the edit dialog, and the activation confirm flow; renders the presenter.
 */
export function UserDetail({ userId, onBack }: UserDetailProps) {
  const { t } = useTranslation();
  const { data, refetch } = useUserDetail(userId);
  const { retry, processing: preparingLogin } = useRetryUserLoginPreparation(refetch);
  const { onActivate, onDeactivate, processing } = useUserActivation();
  const [pendingAction, setPendingAction] = useState<"activate" | "deactivate" | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const { user, firebaseUser } = data?.cnutGetUser ?? {};
  const detail = useMemo(() => (user ? toUserDetailUI(user) : null), [user]);

  if (!detail) {
    return (
      <div className="rounded-[var(--radius-md)] border border-border-default bg-white px-5 py-12 text-center text-14-regular text-fg-2">
        {t("ui.notFound")}
      </div>
    );
  }

  const handleConfirm = async () => {
    if (pendingAction === "activate") {
      await onActivate({ id: detail.id });
    } else if (pendingAction === "deactivate") {
      await onDeactivate({ id: detail.id });
    }
    setPendingAction(null);
  };

  return (
    <>
      <UserDetailPresenter
        loginPreparationPending={data?.cnutGetUser.loginPreparationPending ?? false}
        preparingLogin={preparingLogin}
        onRetryLoginPreparation={async () => {
          await retry(userId);
        }}
        user={detail}
        firebaseUser={firebaseUser ?? null}
        onBack={onBack}
        onEdit={() => setEditOpen(true)}
        onActivate={() => setPendingAction("activate")}
        onDeactivate={() => setPendingAction("deactivate")}
        pendingAction={pendingAction}
        confirmLabel={
          pendingAction === "activate"
            ? t("ui.activateX", { x: detail.email })
            : t("ui.deactivateX", { x: detail.email })
        }
        processing={processing}
        onConfirm={handleConfirm}
        onCancelConfirm={() => setPendingAction(null)}
      />
      <UserFormDialog open={editOpen} onOpenChange={setEditOpen} userId={detail.id} />
    </>
  );
}
